import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';


// SERVICES
import { RefreshService } from 'src/app/refresh.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { ReferralsService, GetReferralsRequest } from './referrals.service';

// CONSTANTS
import { Urls } from 'src/app/lib/urls';
import {
  REFERRAL_TABLE_SUB_HEADERS,
  ACCOUNT_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from 'src/app/lib/constants/referral.constants';

// COMPONENTS (FOR MODAL)
import { ViewReferalComponent } from './view-referal/view-referal.component';

@Component({
  selector: 'app-features-referal',
  templateUrl: './features-referal.component.html',
  styleUrls: ['./features-referal.component.scss'],
})
export class FeaturesReferalComponent implements OnInit, OnDestroy {
  showTable = true;
  private destroy$ = new Subject<void>();

  public form!: UntypedFormGroup;
  searchForm!: UntypedFormGroup;
  limitForm!: UntypedFormGroup;

  // ─── Dropdown Options ──────────────────────────────────────────────────────

  statusOptions      = STATUS_OPTIONS;
  accountTypeOptions = ACCOUNT_TYPE_OPTIONS;

  /** Populated dynamically from the API via getAccountsTiers() */
  tierOptions: { label: string; value: any }[] = [];

  // ─── Dropdown State ────────────────────────────────────────────────────────

  statusType: any       = null;
  statusType_text: any  = null;
  accountType: any      = null;
  accountType_text: any = null;
  tier: any             = null;
  tier_text: any        = null;

  // ─── Table ─────────────────────────────────────────────────────────────────

  tableSubHeader = REFERRAL_TABLE_SUB_HEADERS;

  // ─── Data ──────────────────────────────────────────────────────────────────

  itemLists: any    = [];
  submitted         = false;
  isLoading         = false;
  fileName          = '';
  filterFields: GetReferralsRequest | null = null;

  // ─── Pagination ────────────────────────────────────────────────────────────

  size                         = 10;
  total_pages                  = 0;
  current_page                 = 1;
  pages_array: number[] | null = [];
  total                        = 0;

  // ─── Search & Limit ────────────────────────────────────────────────────────

  search_keyword = '';
  searchKey: any  = null;
  limitKey: any   = null;

  // ─── Sorting ───────────────────────────────────────────────────────────────

  sort_column               = 'dateCreated';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // ─── Constructor ───────────────────────────────────────────────────────────

  constructor(
    private fb: UntypedFormBuilder,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
    private refreshService: RefreshService,
    private dialog: MatDialog,
    private referralService: ReferralsService,
    private request: RequestsService
  ) {
    this.initializeForm();
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.spinner.hide().catch((err) => console.error('Spinner hide error on init:', err));
    this.loadTierOptions();

    this.refreshService.resetTable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetTableState();
      this.showTable = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.spinner.hide().catch((err) => console.error('Spinner hide error on destroy:', err));
  }

  // ─── Load Tier Options dynamically ────────────────────────────────────────

  private loadTierOptions(): void {
    this.referralService
      .getAccountsTiers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.result) {
            this.tierOptions = (res.result as any[]).map((t: any) => ({
              label: `Tier ${t.tierId} (${t.tierName})`,
              value: t.tierId, 
            }));
          }
        },
        error: () => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs`,
            'Something went wrong'
          );
        },
      });
  }

  // ─── Form Initialization ───────────────────────────────────────────────────

  initializeForm(): void {
    this.form = this.fb.group({
      firstName:   [null],
      middleName:  [null],
      lastName:    [null],
      phoneNumber: [null],
    });

    this.searchForm = this.fb.group({ search: [''] });
    this.limitForm  = this.fb.group({ limit: [this.size] });

    this.searchKey = this.debounce.debounce(() => {
      this.current_page   = 1;
      this.search_keyword = this.searchForm.value.search;
      if (this.filterFields) { this.get_filtered_list(); }
    }, 500);

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size         = this.limitForm.value.limit;
      if (this.filterFields) { this.get_filtered_list(); }
    }, 500);
  }

  // ─── Build Request Payload ────────────────────────────────────────────────

  private buildRequestParams(includeSearch = false): GetReferralsRequest {
    const params: GetReferralsRequest = {
      firstName:   this.form.controls['firstName'].value   || null,
      middleName:  this.form.controls['middleName'].value  || null,
      lastName:    this.form.controls['lastName'].value    || null,
      phoneNumber: this.form.controls['phoneNumber'].value || null,
      status:      this.statusType   || null,
      accountType: this.accountType  || null,
      tierId:        this.tier         || null,
      sortBy:      { [this.sort_column]: this.sort_type },
    };

    if (includeSearch && this.search_keyword) {
      params.search = this.search_keyword;
    }

    return params;
  }

  // ─── Submit ────────────────────────────────────────────────────────────────

  submit(): void {
    this.isLoading    = true;
    this.current_page = 1;
    this.pages_array  = [];
    this.showTable    = true;

    const params = this.buildRequestParams();
    this.filterFields = params;
    localStorage.setItem('referralList', JSON.stringify(params));

    this.spinner.show().catch((err) => console.error(err));

    this.referralService
      .getReferralsAccounts(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.handleListResponse(res);
          this.submitted = true;
          this.form.reset();
          this.resetDropdowns();
        },
        error: (err) => {
          this.toastr.error('Failed to fetch referral list.');
          this.isLoading = false;
          this.spinner.hide().catch((e) => console.error(e));
        },
      });
  }

  // ─── Filtered List ─────────────────────────────────────────────────────────

  get_filtered_list(): void {
    this.isLoading   = true;
    this.pages_array = [];

    const params: GetReferralsRequest = {
      ...this.filterFields,
      search: this.search_keyword || null,
      sortBy: { [this.sort_column]: this.sort_type },
    };

    localStorage.setItem('referralList', JSON.stringify(params));

    this.referralService
      .getReferralsAccounts(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.handleListResponse(res),
        error: (err) => {
          this.toastr.error('Failed to fetch referral list.');
          this.isLoading = false;
          this.spinner.hide().catch((e) => console.error(e));
        },
      });
  }

  // ─── Response Handler ──────────────────────────────────────────────────────

  private handleListResponse(res: any): void {
    if (res?.result) {
      this.itemLists   = res.result.data         ?? [];
      this.total       = res.result.totalItems   ?? 0;
      this.total_pages = res.result.totalPages   ?? 0;
      this.current_page = res.result.currentPage ?? this.current_page;
      this.pages_array  = this.paginationHelper.generatePageArray(this.current_page, this.total_pages);
    } else {
      this.itemLists = [];
    }
    this.isLoading = false;
    this.spinner.hide().catch((err) => console.error(err));
  }

  // ─── View Referral Modal ───────────────────────────────────────────────────

  openViewReferral(item: any): void {
    this.dialog.open(ViewReferalComponent, {
      width: '1200px',
      maxHeight: '90vh',
      height: '90vh',
      panelClass: 'referral-view-dialog',
      data: { user: item },
    });
  }

  // ─── Export ────────────────────────────────────────────────────────────────

  async print(): Promise<void> {
    this.spinner.show().catch((err) => console.error(err));

    const current  = new Date();
    this.fileName  = current.toLocaleDateString() + '-referrals.xlsx';
    const params   = JSON.parse(localStorage.getItem('referralList') ?? 'null');

    try {
      await this.request.postBlob(Urls.exportReferralAccounts, params).then((report) => {
        const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
        const url  = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href      = url;
        link.download  = this.fileName;
        link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      });
      this.spinner.hide().catch((err) => console.error(err));
    } catch (err) {
      this.spinner.hide().catch((e) => console.error(e));
    }
  }

  // ─── Dropdown Setters ──────────────────────────────────────────────────────

  setStatusType(text: string, value: string): void {
    this.statusType      = value;
    this.statusType_text = text;
  }

  setAccountType(text: string, value: string): void {
    this.accountType      = value;
    this.accountType_text = text;
  }

  setTier(text: string, value: any): void {
    this.tier      = value;
    this.tier_text = text;
  }

  // ─── Table Controls ────────────────────────────────────────────────────────

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) { this.searchList(); }
  }

  searchList(): void {
    this.current_page   = 1;
    this.search_keyword = this.searchForm.value.search;
    if (this.filterFields) { this.get_filtered_list(); }
  }

  // ─── Sorting & Pagination ──────────────────────────────────────────────────

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    this.spinner.show().catch((err) => console.error(err));
    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column, this.sort_type, sort_column, sort_type,
    );
    this.sort_column = column;
    this.sort_type   = type;
    if (this.submitted && this.filterFields) { this.get_filtered_list(); }
  }

  setPage(num: number): void {
    this.current_page = num;
    if (this.filterFields) { this.get_filtered_list(); }
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  // ─── Resets ────────────────────────────────────────────────────────────────


  refresh(): void {
    this.resetTableState();
    this.showTable = false;
  }

  private resetDropdowns(): void {
    this.statusType       = null;
    this.statusType_text  = null;
    this.accountType      = null;
    this.accountType_text = null;
    this.tier             = null;
    this.tier_text        = null;
  }

  private resetTableState(): void {
    this.form.reset();
    this.searchForm.reset({ search: '' });
    this.limitForm.reset({ limit: this.size });
    this.resetDropdowns();
    this.itemLists      = [];
    this.submitted      = false;
    this.isLoading      = false;
    this.fileName       = '';
    this.filterFields   = null;
    this.size           = 10;
    this.total_pages    = 0;
    this.current_page   = 1;
    this.pages_array    = null;
    this.total          = 0;
    this.search_keyword = '';
    localStorage.setItem('referralList', JSON.stringify(null));
  }
}
